import type { MaintenanceWarning } from "@bing/shared";
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { faDigits } from "../i18n/digits";
import { fa } from "../i18n/fa";
import { Button } from "./Button";
import { useBandBottom } from "./EndCardShell";

/**
 * PRE-DEPLOY MAINTENANCE WARNING — the app-wide apology modal.
 *
 * An admin fires this from the bot panel before deploying (a deploy briefly closes every live
 * online table). The presence socket pushes it to every in-app user; App.tsx renders this while
 * `maintenanceWarning` is set — everywhere EXCEPT the game view, which gets
 * {@link MaintenanceWarningNotice} instead (a scrim over a live table costs the table). It is a
 * HEADS-UP ONLY — it disconnects no one — so it has a single
 * «متوجه شدم» acknowledge button (no destructive choice, no backdrop dismiss: the user must see
 * it), and dismissal is purely client-side. Matches the app's dialog look (see `ConfirmDialog`).
 */
export function MaintenanceWarningModal({
  etaMinutes,
  onDismiss,
}: {
  /** Optional «تقریباً N دقیقهٔ دیگر» hint carried on the push. */
  etaMinutes?: number;
  onDismiss: () => void;
}): ReactNode {
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-label={fa.maintenance.warning.title}
      dir="rtl"
      style={{
        position: "fixed",
        inset: 0,
        // Above every in-app overlay (reconnect/game-switch/win) so the heads-up is never hidden.
        zIndex: 90,
        display: "grid",
        placeItems: "center",
        padding: 20,
        background: "rgba(0,0,0,.6)",
        animation: "bg-fadein 0.2s ease-out",
      }}
    >
      <div
        style={{
          background: "var(--surface)",
          border: "1px solid var(--hairline)",
          borderRadius: "var(--radius-lg)",
          padding: "26px 22px",
          width: "100%",
          maxWidth: 340,
          textAlign: "center",
          boxShadow: "0 30px 70px rgba(0,0,0,.5)",
          animation: "bg-pop .3s ease",
        }}
      >
        <div style={{ fontSize: 40, marginBottom: 6 }} aria-hidden>
          🔧
        </div>
        <h2 style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 800 }}>
          {fa.maintenance.warning.title}
        </h2>
        <p style={{ margin: "0 0 12px", fontSize: 14, lineHeight: 1.9, color: "var(--hint)" }}>
          {fa.maintenance.warning.body}
        </p>
        {etaMinutes !== undefined ? (
          <p style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: "var(--text)" }}>
            {fa.maintenance.warning.eta(faDigits(etaMinutes))}
          </p>
        ) : null}
        <Button variant="primary" onClick={onDismiss}>
          {fa.maintenance.warning.dismiss}
        </Button>
      </div>
    </div>
  );
}

/**
 * THE SAME WARNING AT A TABLE — A NOTICE, NEVER A MODAL (review, 2026-10-04: 6 of 6 measured).
 *
 * The modal above is a full-screen scrim, and a scrim over a live table is not a heads-up: a tap on
 * a hand card or on «خروج» landed in it while the turn timers kept running, so the server auto-played
 * the player's turns and then REMOVED them for idling — a recorded loss at a public table of four, a
 * forfeit at two. The warning exists to stop a deploy costing anyone their table, so it must never
 * be the thing that costs it.
 *
 * App.tsx renders this one in the game view, inside `TopNoticeStack` — the one top-of-screen
 * positioner, below the header band, which is `pointer-events: none`, so the felt underneath stays
 * playable through the notice's whole box. ⚠️ Only the «متوجه شدم» button takes a tap, and it must
 * SAY so (`pointer-events: auto`): pointer-events is inherited, so without it the tap falls through
 * to the felt and the notice can never be dismissed. No backdrop, no `aria-modal`: it announces
 * itself as a status, the way the table's other notices do.
 *
 * ⚠️ AND IT IS SMALL, AND IT GOES BY ITSELF (staging probe, 2026-10-05). That slot is the top of
 * the felt: the full card — title, the long apology, the ETA, a full-size button — ran y≈60–258 at
 * 390×844, over the حکم partner's seat (avatar, name, timer ring) for as long as nobody tapped it. So
 * at a table it is ONE pill: the title and the ETA as one line (it wraps only where the screen is too
 * narrow for it), a small «متوجه شدم», no body — and it dismisses itself
 * {@link MAINTENANCE_NOTICE_TABLE_MS} after the PUSH, because whatever it covers matters for the
 * whole of the match. The timer belongs to the push (`warning`'s identity, which the store keeps
 * until the next one): an unrelated re-render must not restart it, and a second push must. Leaving
 * the table first unmounts it with its timer, and the lobby's modal (unchanged) takes over.
 *
 * ⚠️ AND IT NEVER COVERS A BAND THAT GROWS (card-98 item 13, operator 2026-10-27: «the maintenance
 * warning covers the bottom few px of «دست n» at ×1.3/×1.5»). The stack's slot is the 40 px band's
 * bottom + 8; شلم's header grows with the text zoom to keep «دست n» under its title (card-67 B1: 52 /
 * 60 px at ×1.3 / ×1.5), and the slot covered 4 / 12 px of that line. A band taller than the slot
 * says so (`data-notice-band`), and the pill moves down by what the slot falls short of that band's
 * MEASURED bottom + {@link NOTICE_BAND_AIR} (the end card's own measure, `useBandBottom`) — 0
 * everywhere else, so every other table, and the stack's other notices, are exactly where they were.
 * Opted in (operator, 2026-10-28): شلم's header, and بی‌دل's and شطرنج's, whose 52 px bands the slot
 * covered by 4 / 17 / 26 px at ×1 / ×1.3 / ×1.5 — and حکم's (card-41), whose score box grows with the
 * zoom (52 px at ×1.3): the slot covered its bottom 4 px at 360×640.
 */
export function MaintenanceWarningNotice({
  warning,
  onDismiss,
}: {
  warning: MaintenanceWarning;
  onDismiss: () => void;
}): ReactNode {
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;
  useEffect(() => {
    const id = window.setTimeout(() => dismiss.current(), MAINTENANCE_NOTICE_TABLE_MS);
    return () => window.clearTimeout(id);
  }, [warning]);
  // how far below the stack's slot the opted-in band ends (its parent IS the slot: `TopNoticeStack`)
  const boxRef = useRef<HTMLDivElement>(null);
  const bandBottom = useBandBottom(boxRef, NOTICE_BAND);
  const [push, setPush] = useState(0);
  useLayoutEffect(() => {
    const slot = boxRef.current?.parentElement;
    setPush(
      slot && bandBottom !== null
        ? Math.max(0, Math.ceil(bandBottom + NOTICE_BAND_AIR - slot.getBoundingClientRect().top))
        : 0,
    );
  }, [bandBottom]);
  const line =
    warning.etaMinutes !== undefined
      ? `${fa.maintenance.warning.title} · ${fa.maintenance.warning.eta(faDigits(warning.etaMinutes))}`
      : fa.maintenance.warning.title;
  return (
    <div
      ref={boxRef}
      role="status"
      aria-label={fa.maintenance.warning.title}
      dir="rtl"
      style={{
        ...(push > 0 ? { marginTop: push } : {}),
        display: "flex",
        alignItems: "center",
        gap: 10,
        maxWidth: "100%",
        paddingBlock: 6,
        paddingInlineStart: 14,
        paddingInlineEnd: 6,
        borderRadius: 18,
        background: "var(--surface)",
        border: "1px solid var(--hairline)",
        color: "var(--text)",
        fontSize: 13,
        fontWeight: 700,
        lineHeight: 1.6,
        boxShadow: "0 8px 22px rgba(0,0,0,.35)",
        animation: "bg-pop 0.3s ease",
      }}
    >
      <span style={{ minWidth: 0 }}>{line}</span>
      <Button
        variant="primary"
        onClick={onDismiss}
        style={{ pointerEvents: "auto", flex: "none", padding: "5px 12px", fontSize: 12 }}
      >
        {fa.maintenance.warning.dismiss}
      </Button>
    </div>
  );
}

/** How long the warning stays on a TABLE before it goes by itself — see {@link MaintenanceWarningNotice}. */
export const MAINTENANCE_NOTICE_TABLE_MS = 12_000;

/** The bands the table warning clears — opted in, never assumed: `data-notice-band` (the شلم, بی‌دل,
 *  شطرنج and حکم headers). */
const NOTICE_BAND = ["[data-notice-band]"] as const;
/** The slot's own air under the 40 px band (`TopNoticeStack`: 58 = 10 + 40 + 8). */
const NOTICE_BAND_AIR = 8;
